import React, { useEffect, useRef, useState } from 'react'
import { Button, Drawer, Icon, Spin, message as antMessage } from 'antd'

import FinalizedReportModal from './FinalizedReportModal'
import { MAX_REFERENCE_FILE_SIZE } from './constants'
import {
  ConversationCard,
  MessageList,
  PhaseWorkspace,
  ProgressPreview,
  StoryboardPlanPreview,
  VisionDirectionCard
} from './components'
import { ImagePreview } from '../../../../../../components'
import {
  formatCount,
  getFriendlyErrorMessage,
  getGeneratedAssets,
  getPhaseIndex,
  getPhaseCount,
  getNextPhaseLabel,
  getPhaseView,
  getPhaseStatus,
  getReferenceKind,
  getStoryboardPlanCount,
  getStatusLabel,
  isPhaseSelectable,
  isAcceptedReferenceFile,
  isEntityVerified,
  revokePendingReferencePreviews
} from './utils'
import './CreativeDirectionChat.scss'

const getMessagePhase = message => message?.phase || 'vision'
const ACTIVE_CHAT_GENERATION_STATUSES = [
  'pending',
  'queued',
  'processing',
  'running',
  'in_progress'
]
const getAssetPreviewUrl = asset =>
  asset?.image_url ||
  asset?.imageUrl ||
  asset?.thumbnail_url ||
  asset?.thumbnailUrl ||
  ''
const getAssetLabel = asset =>
  asset?.name ||
  asset?.filename ||
  asset?.entity_name ||
  asset?.entityName ||
  'Generated visual'

const CreativeDirectionChat = ({
  sessionId,
  messages,
  metadata,
  phaseSteps,
  phaseData,
  creativeGuideline,
  progressPercent,
  creativeStatement,
  acceptedExtensions,
  isCreatingSession,
  isStreaming,
  isSynthesizingVision,
  isUploadingReference,
  referenceUploadProgress,
  referenceUploadStatus,
  referenceUploadMessage,
  isGenerationPolling,
  generationProgress,
  generationStatus,
  generationMessage,
  isLockingVision,
  isVisionLocked,
  isFinalized,
  phaseBusyAction,
  error,
  onStopAgentTurn,
  onSendMessage,
  onUploadRequestedReference,
  onSynthesizeVision,
  onLockVision,
  onExtractPhase,
  onVerifyEntity,
  onVerifyAllPhase,
  onGenerateStoryboardPlan,
  onRefreshGenerationStatus,
  onAdvancePhase,
  onFinalizeCreativeDirection,
  onStartPrompt,
  onRetryCreateSession,
  onSkipToShotList
}) => {
  const [draftMessage, setDraftMessage] = useState('')
  const [pendingReferences, setPendingReferences] = useState([])
  const [isFinalizedReportOpen, setIsFinalizedReportOpen] = useState(false)
  const [detailDrawerPhase, setDetailDrawerPhase] = useState('')
  const [isActiveWorkspaceCollapsed, setIsActiveWorkspaceCollapsed] = useState(
    false
  )
  const scrollRef = useRef(null)
  const pendingReferencesRef = useRef([])
  const currentPhase = metadata.currentPhase || 'vision'
  const isVisionPhase = currentPhase === 'vision'

  const togglePhaseDetails = phase => {
    setDetailDrawerPhase(prevPhase => (prevPhase === phase ? '' : phase))
  }

  const handleToggleArtifacts = () => {
    setDetailDrawerPhase(prevPhase => (prevPhase ? '' : currentPhase))
  }

  useEffect(() => {
    pendingReferencesRef.current = pendingReferences
  }, [pendingReferences])

  useEffect(() => {
    setIsActiveWorkspaceCollapsed(false)
  }, [currentPhase])

  useEffect(
    () => () => {
      revokePendingReferencePreviews(pendingReferencesRef.current)
    },
    []
  )

  useEffect(() => {
    const scrollToBottom = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight
      }
    }

    const frameId = window.requestAnimationFrame(scrollToBottom)
    const timeoutId = window.setTimeout(scrollToBottom, 120)

    return () => {
      window.cancelAnimationFrame(frameId)
      window.clearTimeout(timeoutId)
    }
  }, [
    creativeStatement,
    currentPhase,
    isStreaming,
    isSynthesizingVision,
    messages,
    phaseData,
    phaseBusyAction,
    error
  ])

  const handleAttachReference = file => {
    if (!file) return false

    if (!isAcceptedReferenceFile(file, acceptedExtensions)) {
      antMessage.error('Attach an image or video reference file.')
      return false
    }

    if (file.size > MAX_REFERENCE_FILE_SIZE) {
      antMessage.error('Reference files must be 50 MB or less.')
      return false
    }

    const fileKey =
      file.uid || `${file.name}-${file.size}-${file.lastModified || Date.now()}`
    const previewUrl =
      getReferenceKind(file) === 'image' && window.URL?.createObjectURL
        ? window.URL.createObjectURL(file)
        : ''

    setPendingReferences(prev => {
      if (prev.some(item => item.fileKey === fileKey)) {
        if (previewUrl && window.URL?.revokeObjectURL) {
          window.URL.revokeObjectURL(previewUrl)
        }
        return prev
      }

      return [
        ...prev,
        {
          id: `reference-${fileKey}`,
          fileKey,
          file,
          previewUrl
        }
      ]
    })

    return false
  }

  const handleRemovePendingReference = referenceId => {
    setPendingReferences(prev =>
      prev.filter(item => {
        const shouldKeep = item.id !== referenceId
        if (!shouldKeep && item.previewUrl && window.URL?.revokeObjectURL) {
          window.URL.revokeObjectURL(item.previewUrl)
        }
        return shouldKeep
      })
    )
  }

  const handleSubmit = async () => {
    const message = draftMessage.trim()
    if (
      !sessionId ||
      !message ||
      isStreaming ||
      isSynthesizingVision ||
      isCreatingSession ||
      isUploadingReference ||
      isLockingVision ||
      Boolean(phaseBusyAction)
    ) {
      return
    }

    const referencesToUpload = pendingReferences

    setDraftMessage('')

    if (referencesToUpload.length) {
      const uploadedReferenceIds = referencesToUpload.map(item => item.id)
      setPendingReferences(prev =>
        prev.filter(item => !uploadedReferenceIds.includes(item.id))
      )
      revokePendingReferencePreviews(referencesToUpload)
    }

    await onSendMessage(message, {
      files: referencesToUpload.map(item => item.file)
    })
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleStarterPrompt = item => {
    if (
      isStreaming ||
      isSynthesizingVision ||
      isCreatingSession ||
      isLockingVision ||
      Boolean(phaseBusyAction) ||
      !sessionId
    ) {
      return
    }

    if (item.action === 'start') {
      onStartPrompt()
      return
    }

    onSendMessage(item.message)
  }

  if (isCreatingSession) {
    return (
      <div className="creative-direction-chat__loading">
        <Spin size="large" />
        <div>
          <h3>Preparing AI</h3>
          <p>Starting a creative direction session for this story.</p>
        </div>
      </div>
    )
  }

  if (!sessionId && error) {
    return (
      <div className="creative-direction-chat__empty">
        <Icon type="warning" />
        <h3>Creative direction could not start</h3>
        <p>{getFriendlyErrorMessage(error)}</p>
        <Button type="primary" onClick={onRetryCreateSession}>
          Try again
        </Button>
      </div>
    )
  }

  const currentPhaseIndex = getPhaseIndex(phaseSteps, currentPhase)
  const visibleWorkflowPhases = phaseSteps.slice(1, currentPhaseIndex + 1)
  const hasConversation = messages.some(item =>
    String(item.content || '').trim()
  )
  const hasStreamingMessage = messages.some(item => item.streaming)
  const getMessagesForPhase = phase =>
    messages.filter(item => getMessagePhase(item) === phase)
  const latestAssistantMessage = [...messages]
    .reverse()
    .find(item => item.role === 'assistant' && !item.streaming)
  const latestReferenceRequestMessage =
    latestAssistantMessage?.metadata?.referenceRequest
      ? latestAssistantMessage
      : null
  const shouldShowTypingForPhase = phase =>
    phase === currentPhase && isStreaming && !hasStreamingMessage
  const shouldShowVisionTimeline =
    hasConversation || Boolean(creativeStatement) || isSynthesizingVision
  const isChatTurnBusy =
    isStreaming ||
    isSynthesizingVision ||
    isUploadingReference ||
    isLockingVision
  const messageListReferenceProps = {
    activeReferenceMessageId: latestReferenceRequestMessage?.id || '',
    acceptedExtensions,
    phaseBusyAction,
    referenceUploadProgress,
    referenceUploadStatus,
    referenceUploadMessage,
    onUploadRequestedReference
  }
  const previewExpandedPhases = detailDrawerPhase
    ? { [detailDrawerPhase]: true }
    : {}

  const isPhaseGeneratingInChat = phaseKey => {
    const phaseTaskStatus = String(
      phaseData[phaseKey]?.generationTask?.status || ''
    ).toLowerCase()
    const liveStatus = String(generationStatus || '').toLowerCase()

    return (
      phaseBusyAction === `generate:${phaseKey}` ||
      ACTIVE_CHAT_GENERATION_STATUSES.includes(phaseTaskStatus) ||
      (phaseKey === currentPhase &&
        ACTIVE_CHAT_GENERATION_STATUSES.includes(liveStatus))
    )
  }

  const shouldShowActivePhaseWorkspace = phaseKey => {
    if (
      !phaseKey ||
      phaseKey !== currentPhase ||
      isStreaming ||
      hasStreamingMessage ||
      Boolean(phaseBusyAction) ||
      isPhaseGeneratingInChat(phaseKey)
    ) {
      return false
    }

    if (['characters', 'outfits', 'locations'].includes(phaseKey)) {
      const phasePayload = phaseData[phaseKey] || {}
      const assets = getGeneratedAssets(phaseData, phaseKey)
      const hasExtracted = Boolean(phasePayload.extracted || phasePayload.count)
      const entities = phasePayload.entities || []
      const allEntitiesVerified =
        !entities.length || entities.every(isEntityVerified)

      // Only show bar if there's an action to take
      return !hasExtracted || !allEntitiesVerified || Boolean(assets.length)
    }

    if (phaseKey === 'storyboard') {
      const storyboardData = phaseData.storyboard || {}
      const hasPlan = getStoryboardPlanCount(storyboardData.plan) > 0
      const hasGenerated =
        getGeneratedAssets(phaseData, 'storyboard').length > 0

      return !hasPlan || hasGenerated
    }

    if (phaseKey === 'finalized') {
      return !isFinalized || Boolean(creativeGuideline)
    }

    return false
  }

  const renderPhaseWorkspace = ({
    phaseKey,
    isPhaseActive,
    isPhaseExpanded,
    showPhaseActions,
    isDrawerView = false
  }) => (
    <PhaseWorkspace
      currentPhase={phaseKey}
      phaseSteps={phaseSteps}
      phaseData={phaseData}
      creativeGuideline={creativeGuideline}
      isFinalized={isFinalized}
      creativeStatement={creativeStatement}
      hasConversation={hasConversation}
      isStreaming={isStreaming}
      isSynthesizingVision={isSynthesizingVision}
      isLockingVision={isLockingVision}
      isVisionLocked={isVisionLocked}
      isActivePhase={isPhaseActive}
      isSelectedPhase={isPhaseExpanded}
      showActions={showPhaseActions}
      phaseBusyAction={phaseBusyAction}
      acceptedExtensions={acceptedExtensions}
      referenceUploadProgress={referenceUploadProgress}
      referenceUploadStatus={referenceUploadStatus}
      referenceUploadMessage={referenceUploadMessage}
      generationProgress={generationProgress}
      generationStatus={generationStatus}
      generationMessage={generationMessage}
      onExtractPhase={onExtractPhase}
      onVerifyEntity={onVerifyEntity}
      onVerifyAllPhase={onVerifyAllPhase}
      onGenerateStoryboardPlan={onGenerateStoryboardPlan}
      onRefreshGenerationStatus={onRefreshGenerationStatus}
      onUploadRequestedReference={onUploadRequestedReference}
      onAdvancePhase={onAdvancePhase}
      onSynthesizeVision={onSynthesizeVision}
      onLockVision={onLockVision}
      onFinalizeCreativeDirection={onFinalizeCreativeDirection}
      onViewFinalizedReport={() => setDetailDrawerPhase('finalized')}
      onTogglePhase={() => togglePhaseDetails(phaseKey)}
      isDrawerView={isDrawerView}
      showReferenceRequestCard={false}
    />
  )

  const expandedActivePhaseWorkspace =
    !isVisionPhase && shouldShowActivePhaseWorkspace(currentPhase)
      ? renderPhaseWorkspace({
        phaseKey: currentPhase,
        isPhaseActive: true,
        isPhaseExpanded: false,
        showPhaseActions: true
      })
      : null

  const activePhaseWorkspace = expandedActivePhaseWorkspace

  const renderPhaseMarker = phaseKey => (
    <div className="creative-direction-chat__phase-marker">
      <span>{getPhaseView(phaseKey).label}</span>
    </div>
  )

  const getChatVisualSlotCount = phaseKey => {
    if (phaseKey === 'storyboard') return 1

    const phasePayload = phaseData[phaseKey] || {}
    const entityCount = Array.isArray(phasePayload.entities)
      ? phasePayload.entities.length
      : 0
    const fallbackCount =
      getPhaseCount(phaseData, phaseKey) || entityCount || 4

    return Math.max(1, Math.min(fallbackCount, 12))
  }

  const renderChatPhaseVisualStrip = phaseKey => {
    const view = getPhaseView(phaseKey)
    const assets = getGeneratedAssets(phaseData, phaseKey)
    const isGenerating = isPhaseGeneratingInChat(phaseKey)

    if (!assets.length && !isGenerating) return null

    const previewImages = assets
      .map(asset => {
        const imageUrl = getAssetPreviewUrl(asset)
        if (!imageUrl) return null
        return {
          src: imageUrl,
          alt: getAssetLabel(asset)
        }
      })
      .filter(Boolean)
    const expectedSlots = getChatVisualSlotCount(phaseKey)
    const placeholderCount = isGenerating
      ? Math.max(expectedSlots - assets.length, assets.length ? 0 : expectedSlots)
      : 0
    const headerLabel =
      phaseKey === 'storyboard'
        ? isGenerating
          ? 'Generating storyboard image...'
          : 'Generated storyboard image'
        : isGenerating
          ? 'Generating Visuals...'
          : 'Generated Visuals'

    return (
      <section
        className={`creative-direction-chat__chat-artifact creative-direction-chat__chat-artifact--visuals${
          phaseKey === 'storyboard'
            ? ' creative-direction-chat__chat-artifact--single-visual'
            : ''
        }`}
      >
        <div className="creative-direction-chat__chat-artifact-header">
          <span>{headerLabel}</span>
          {!isGenerating ? (
            <strong>{`${formatCount(assets.length)} ready`}</strong>
          ) : null}
        </div>

        <div className="creative-direction-chat__chat-asset-strip">
          {assets.map((asset, index) => {
            const imageUrl = getAssetPreviewUrl(asset)
            const label = getAssetLabel(asset)
            const initialIndex = previewImages.findIndex(
              image => image.src === imageUrl
            )

            return (
              <figure
                className="creative-direction-chat__chat-asset"
                key={asset?.id || imageUrl || `${phaseKey}-${index}`}
              >
                {imageUrl ? (
                  <ImagePreview
                    src={imageUrl}
                    alt={label}
                    images={previewImages}
                    initialIndex={initialIndex >= 0 ? initialIndex : 0}
                    cancelOnUnmount
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <span>
                    <Icon type="picture" />
                  </span>
                )}
              </figure>
            )
          })}
          {Array.from({ length: placeholderCount }).map((_, index) => (
            <figure
              className="creative-direction-chat__chat-asset creative-direction-chat__chat-asset--loading"
              key={`${phaseKey}-loading-${index}`}
            >
              <span aria-label="Generating visual preview" />
            </figure>
          ))}
        </div>
      </section>
    )
  }

  const renderChatAdvanceStatus = () => {
    if (!String(phaseBusyAction || '').startsWith('advance:')) return null

    const sourcePhase = String(phaseBusyAction).replace('advance:', '')
    const sourceLabel = getPhaseView(sourcePhase).label
    const nextLabel = getNextPhaseLabel(phaseSteps, sourcePhase)

    return (
      <section className="creative-direction-chat__chat-artifact creative-direction-chat__chat-artifact--status">
        <div className="creative-direction-chat__chat-artifact-header">
          <Icon type="loading" />
          <span>{`Moving to ${nextLabel}...`}</span>
          <strong>Saving</strong>
        </div>
        <p className="creative-direction-chat__chat-artifact-text">
          {`${sourceLabel} is being saved and ${nextLabel} is being prepared.`}
        </p>
      </section>
    )
  }

  const renderChatStoryboardPlan = () => {
    const storyboardData = phaseData.storyboard || {}
    const planCount = getStoryboardPlanCount(storyboardData.plan)
    const hasPlan = planCount > 0
    const isPlanning =
      phaseBusyAction === 'storyboard:plan' ||
      (currentPhase === 'storyboard' && isStreaming && !hasPlan)

    if (!hasPlan && !isPlanning) return null

    return (
      <section className="creative-direction-chat__chat-artifact creative-direction-chat__chat-artifact--storyboard-plan creative-direction-chat__drawer-doc-section">
        <div className="creative-direction-chat__drawer-doc-title">
          <Icon type="profile" />
          <span>Storyboard Plan</span>
          {hasPlan ? (
            <span className="creative-direction-chat__drawer-doc-count">
              {formatCount(planCount)}
            </span>
          ) : null}
        </div>
        {hasPlan ? (
          <div className="creative-direction-chat__chat-storyboard-plan-body">
            <StoryboardPlanPreview
              plan={storyboardData.plan}
              committedPanels={storyboardData.committedPanels}
              planApproved={Boolean(
                storyboardData.planApproved ||
                  storyboardData.committedPanels ||
                  phaseData.status?.storyboardPlanApproved
              )}
              previewLimit={3}
              showMoreControl={false}
            />
            {planCount > 3 ? (
              <button
                type="button"
                className="creative-direction-chat__chat-artifact-open-btn"
                onClick={() => setDetailDrawerPhase('storyboard')}
              >
                <Icon type="appstore" />
                <span>Open full plan in artifacts</span>
              </button>
            ) : null}
          </div>
        ) : (
          <div className="creative-direction-chat__chat-plan-skeleton">
            {[0, 1, 2, 3].map(index => (
              <span key={index} />
            ))}
          </div>
        )}
      </section>
    )
  }

  const renderStoryboardPhaseTimeline = phaseKey => {
    const phaseMessages = getMessagesForPhase(phaseKey)
    const storyboardPlan = renderChatStoryboardPlan()
    const showTyping = shouldShowTypingForPhase(phaseKey)
    const hasPlan = getStoryboardPlanCount(phaseData.storyboard?.plan) > 0

    if (!storyboardPlan) {
      return (
        <MessageList
          messages={phaseMessages}
          showTyping={showTyping}
          {...messageListReferenceProps}
        />
      )
    }

    if (!hasPlan) {
      return (
        <>
          <MessageList
            messages={phaseMessages}
            showTyping={showTyping}
            {...messageListReferenceProps}
          />
          {storyboardPlan}
        </>
      )
    }

    const anchorIndex = phaseMessages.findIndex(
      message => message?.role === 'assistant' && !message?.streaming
    )

    if (anchorIndex < 0) {
      return (
        <>
          {storyboardPlan}
          <MessageList
            messages={phaseMessages}
            showTyping={showTyping}
            {...messageListReferenceProps}
          />
        </>
      )
    }

    return (
      <>
        <MessageList
          messages={phaseMessages.slice(0, anchorIndex + 1)}
          showTyping={false}
          {...messageListReferenceProps}
        />
        {storyboardPlan}
        <MessageList
          messages={phaseMessages.slice(anchorIndex + 1)}
          showTyping={showTyping}
          {...messageListReferenceProps}
        />
      </>
    )
  }

  const drawerPhaseView = detailDrawerPhase
    ? getPhaseView(detailDrawerPhase)
    : null

  const renderVisionTimeline = ({ isArchived = false } = {}) => (
    <>
      <MessageList
        messages={getMessagesForPhase('vision')}
        showTyping={!isArchived && shouldShowTypingForPhase('vision')}
        {...messageListReferenceProps}
      />
      <VisionDirectionCard
        creativeStatement={creativeStatement}
        hasConversation={hasConversation}
        isStreaming={isStreaming}
        isSynthesizingVision={isSynthesizingVision}
        isLockingVision={isLockingVision}
        isVisionLocked={isVisionLocked}
        isArchived={isArchived}
        onSynthesizeVision={onSynthesizeVision}
        onLockVision={onLockVision}
      />
    </>
  )

  const timelinePanel =
    isVisionPhase && !shouldShowVisionTimeline ? null : isVisionPhase ? (
      renderVisionTimeline()
    ) : (
      <>
        {shouldShowVisionTimeline ? (
          <React.Fragment key="vision">
            {renderPhaseMarker('vision')}
            {renderVisionTimeline({ isArchived: true })}
          </React.Fragment>
        ) : null}
        {visibleWorkflowPhases.map(phaseStep => {
          const phaseKey = phaseStep.key

          return (
            <React.Fragment key={phaseKey}>
              {renderPhaseMarker(phaseKey)}
              {phaseKey === 'storyboard' ? (
                renderStoryboardPhaseTimeline(phaseKey)
              ) : (
                <MessageList
                  messages={getMessagesForPhase(phaseKey)}
                  showTyping={shouldShowTypingForPhase(phaseKey)}
                  {...messageListReferenceProps}
                />
              )}
              {renderChatPhaseVisualStrip(phaseKey)}
            </React.Fragment>
          )
        })}
        {renderChatAdvanceStatus()}
      </>
    )

  const artifactDrawer = (
    <Drawer
      className="creative-direction-chat__phase-drawer"
      visible={Boolean(detailDrawerPhase)}
      onClose={() => setDetailDrawerPhase('')}
      placement="right"
      width="90%"
      closable={false}
      bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      maskClosable={true}
    >
      {detailDrawerPhase && (
        <aside
          className="creative-direction-chat__artifact-panel creative-direction-chat__artifact-panel--drawer"
          aria-label="Creative direction artifacts"
        >
          {/* Top bar */}
          <div className="creative-direction-chat__artifact-topbar">
            <div className="creative-direction-chat__artifact-topbar-left">
              <span className="creative-direction-chat__artifact-topbar-label">Artifacts</span>
            </div>
            <div className="creative-direction-chat__artifact-topbar-center">
              <span className="creative-direction-chat__artifact-topbar-title">
                {drawerPhaseView ? drawerPhaseView.label : 'Phase workspace'}
              </span>
            </div>
            <div className="creative-direction-chat__artifact-topbar-right">
              <button
                className="creative-direction-chat__artifact-close-btn"
                aria-label="Close artifacts"
                onClick={() => setDetailDrawerPhase('')}
              >
                <Icon type="close" />
              </button>
            </div>
          </div>

          {/* Body: sidebar + content */}
          <div className="creative-direction-chat__artifact-body">
            {/* Left sidebar — file-system navigation */}
            <nav
              className="creative-direction-chat__artifact-sidebar"
              aria-label="Artifact sections"
            >
              <div className="creative-direction-chat__artifact-sidebar-heading">Sections</div>
              {phaseSteps.map(step => {
                const view = getPhaseView(step.key)
                const status = getPhaseStatus({
                  phaseSteps,
                  currentPhase,
                  phase: step.key,
                  isFinalized
                })
                const canSelect = isPhaseSelectable({
                  phaseSteps,
                  currentPhase,
                  phase: step.key,
                  isFinalized
                })
                const isSelected = detailDrawerPhase === step.key

                return (
                  <button
                    type="button"
                    className={`creative-direction-chat__artifact-nav-item creative-direction-chat__artifact-nav-item--${status}${
                      isSelected ? ' creative-direction-chat__artifact-nav-item--active' : ''
                    }`}
                    disabled={!canSelect}
                    key={step.key}
                    aria-current={isSelected ? 'page' : undefined}
                    onClick={() => canSelect && setDetailDrawerPhase(step.key)}
                  >
                    <span className="creative-direction-chat__artifact-nav-icon">
                      <Icon type={view.icon} />
                    </span>
                    <span className="creative-direction-chat__artifact-nav-label">
                      {view.shortLabel}
                    </span>
                    <span className="creative-direction-chat__artifact-nav-status">
                      {getStatusLabel(status)}
                    </span>
                  </button>
                )
              })}
            </nav>

            {/* Right content area */}
            <div className="creative-direction-chat__artifact-content">
              {renderPhaseWorkspace({
                phaseKey: detailDrawerPhase,
                isPhaseActive: detailDrawerPhase === currentPhase,
                isPhaseExpanded: true,
                showPhaseActions:
                  detailDrawerPhase === currentPhase ||
                  detailDrawerPhase === 'storyboard' ||
                  detailDrawerPhase === 'finalized',
                isDrawerView: true
              })}
            </div>
          </div>
        </aside>
      )}
    </Drawer>
  )

  return (
    <div
      className={`creative-direction-chat${
        detailDrawerPhase ? ' creative-direction-chat--artifacts-open' : ''
      }`}
    >
      <ProgressPreview
        phaseSteps={phaseSteps}
        currentPhase={currentPhase}
        expandedPhases={previewExpandedPhases}
        progressPercent={progressPercent}
        isFinalized={isFinalized}
        onPhaseSelect={togglePhaseDetails}
      />

      <main className="creative-direction-chat__main">
        <div className="creative-direction-chat__workspace">
          <ConversationCard
            sessionId={sessionId}
            messages={messages}
            timelinePanel={timelinePanel}
            scrollRef={scrollRef}
            errorMessage={
              error && sessionId ? getFriendlyErrorMessage(error) : ''
            }
            isCreatingSession={isCreatingSession}
            isStreaming={isStreaming}
            isSynthesizingVision={isSynthesizingVision}
            isUploadingReference={isUploadingReference}
            isGenerationPolling={isGenerationPolling}
            isLockingVision={isLockingVision}
            phaseBusyAction={phaseBusyAction}
            isActiveWorkspaceCollapsed={isActiveWorkspaceCollapsed}
            acceptedExtensions={acceptedExtensions}
            pendingReferences={pendingReferences}
            activeWorkspace={activePhaseWorkspace}
            draftMessage={draftMessage}
            isArtifactsOpen={Boolean(detailDrawerPhase)}
            onDraftMessageChange={setDraftMessage}
            onAttachReference={handleAttachReference}
            onRemovePendingReference={handleRemovePendingReference}
            onSubmit={handleSubmit}
            onStopAgentTurn={onStopAgentTurn}
            onKeyDown={handleKeyDown}
            onStarterPrompt={handleStarterPrompt}
            onViewArtifacts={handleToggleArtifacts}
            onSkipToShotList={onSkipToShotList}
            isSkipDisabled={
              isCreatingSession ||
              isStreaming ||
              isSynthesizingVision ||
              isUploadingReference ||
              isLockingVision ||
              Boolean(phaseBusyAction)
            }
          />
          {artifactDrawer}
        </div>
      </main>

      <FinalizedReportModal
        visible={isFinalizedReportOpen}
        report={creativeGuideline}
        onClose={() => setIsFinalizedReportOpen(false)}
      />
    </div>
  )
}

export default CreativeDirectionChat
